import { hasPermission } from '@lognai/plugin-sdk';
/**
 * MainLayout Component
 *
 * Main application layout using synapse design system AppShell.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppShell,
  IconButton,
  Breadcrumbs,
  useTheme,
  type NavItem,
} from '@lognai/synapse-design-system-v2';
import { getAllRoutes, type NavItemConfig } from '@lognai/frontend-core';
// Phosphor via the design system, not lucide: the incoming branch predates the
// icon migration in 352e3e0c, which replaced lucide across 248 files so that
// navigation could render in the duotone weight. `ShoppingCart` is the only
// icon that branch needed and this one did not already import.
//
// `ReceiptText` is deliberately absent: the incoming branch imported it for a
// hardcoded Freight Audit nav item, which is now built from route metadata
// (freight-audit-ui/src/routes.ts:16 resolves the icon by name).
import {
  Inbox,
  PanelsTopLeft,
  Settings,
  ShoppingCart,
  Sparkles,
} from '@lognai/synapse-design-system-v2/icons';
import { buildBreadcrumbs, deriveSectionEntity, type RouteParent } from './breadcrumbs';
import { useAuth } from '../hooks/useAuth';
import { NAV_SECTIONS, PLUGIN_NAV_METADATA, useNavigation } from '../hooks/useNavigation';
import { useIsExternalPartner } from '../hooks/useIsExternalPartner';
import { useSidebarMode } from '../hooks/useSidebarMode';
import { useSidebarCollapsed } from '../hooks/useSidebarCollapsed';
import { UserMenu } from '../components/UserMenu';
import { Logo, LogoMark } from '../components/Logo';
import { CompanyBrandImage } from '../components/CompanyBrandImage';
import { LiveDot } from '../components/LiveDot';
import { PoweredByLognAI } from '../components/PoweredByLognAI';
import { resolveCompanyBrand } from '../utils/company-brand';
import {
  AskLognAISearchBar,
  ChatSidebar,
  CommandPalette,
  registerEntityLabel,
  setActingCompany,
  useEntityLabel,
  useKeyboardShortcut,
} from '@lognai/plugin-ai-core-ui';
import { getCompanySummary } from '../services/api/companies';
import { getUserSummary } from '../services/api/users';
import { getTemporaryPermitsEntityLabel, type TemporaryPermitsEntityType } from '../services/api/temporary-permits';
import { RouteOutcomeProvider } from './route-outcome';
import { deriveAiPageContext, isRegisteredStaticSegment } from './ai-page-context';

/**
 * `/companies/<id>/…` is a SHELL route, not a plugin one, so nothing had
 * registered a resolver for its id segment and the breadcrumb fell back to the
 * generic "Details" crumb — and before that fallback existed, to the raw
 * internal id title-cased into the trail: "Home › Companies ›
 * 1770226980807 M9kqhh2kp › Members" (QA ledger PERM-003).
 *
 * Registered at module scope, next to the routes that need it, rather than in
 * admin-ui: that plugin owns the company screens but does not depend on the
 * entity-label registry, and the shell already does.
 */
registerEntityLabel('admin', 'company', async (id) => {
  try {
    return (await getCompanySummary(id)).name ?? null;
  } catch {
    return null;
  }
});

/**
 * `/admin/users/<id>/roles` and `/access`, for the same reason — the id
 * segment had no resolver and the crumb read "Details" where the person's
 * name belongs (QA ledger ADM-002).
 *
 * Falls back to the email when a user has no name set, because an address
 * still identifies the person and "Details" does not.
 */
registerEntityLabel('admin', 'user', async (id) => {
  try {
    const user = await getUserSummary(id);
    return user.fullName || user.email || null;
  } catch {
    return null;
  }
});

/**
 * Temporary Permits' request, permit, guarantee and asset pages — the id segment resolves
 * to the record's own reference (TRF-2026-0212, TP-2026-0044) for the crumb.
 * Registered here, like the admin ones above, because temporary-permits-ui does not depend
 * on the entity-label registry and the shell must not import temporary-permits-ui.
 */
(['request', 'case', 'guarantee', 'asset'] as TemporaryPermitsEntityType[]).forEach((type) =>
  registerEntityLabel('temporary-permits', type, async (id) => {
    try {
      return await getTemporaryPermitsEntityLabel(type, id);
    } catch {
      return null;
    }
  })
);

/**
 * Plugins mounted under their own prefix whose pages are one block of a rail
 * section. The prefix stops being a crumb of its own and becomes the section —
 * "Home › Freight › Requests" — named from the same metadata that draws the
 * rail, so the two cannot disagree.
 */
const SECTION_ROOT_PLUGINS = ['temporary-permits'];
const SECTION_ROOTS: Record<string, string> = Object.fromEntries(
  SECTION_ROOT_PLUGINS.flatMap((id) => {
    const section = PLUGIN_NAV_METADATA[id]?.section;
    const name = section ? NAV_SECTIONS[section]?.displayName : undefined;
    return name ? [[id, name]] : [];
  })
);

// Adapter component to make React Router's Link work with AppShell's href prop
const RouterLink = React.forwardRef<HTMLAnchorElement, any>(({ href, children, ...props }, ref) => (
  <Link to={href} ref={ref} {...props}>
    {children}
  </Link>
));
RouterLink.displayName = 'RouterLink';


export default function MainLayout() {
  const { user, company } = useAuth();
  const isExternalPartner = useIsExternalPartner();
  const { theme } = useTheme();
  const { mode: sidebarMode, setMode: setSidebarMode } = useSidebarMode();
  const { collapsed: sidebarCollapsed, toggleCollapsed } = useSidebarCollapsed();

  // The sidebar appearance follows the app theme: one switcher controls both.
  useEffect(() => {
    setSidebarMode(theme);
  }, [theme, setSidebarMode]);

  // White-label: the active company's platform logo, if any, replaces the
  // LognAI mark in the rail, following the sidebar's own surface. A platform
  // admin has no company and the auth pages don't mount this layout, so both
  // keep the LognAI logo.
  const brand = resolveCompanyBrand(company, sidebarMode);
  const logoVariant = sidebarMode === 'light' ? 'dark' : 'white';
  const lognaiMark = <LogoMark variant={logoVariant} className="h-8 w-8" />;
  const location = useLocation();
  const navigate = useNavigate();
  /**
   * May this user actually use the AI assistant?
   *
   * The Ask bar and chat sidebar are imported straight into this shell, so they
   * rendered for everyone — and `AskLognAISearchBar` lists threads on mount, so
   * a user without AI took a 403 on every single page load.
   *
   * Gated on the PERMISSION, not the plugin list. `company.effectivePlugins`
   * includes ai-core for companies whose roles carry no `ai:*` permission at
   * all (the server adds implicit plugins on top of a company's own enabled
   * set, while role provisioning used only the enabled set) — so the plugin
   * list says "loaded" where the API says "forbidden". `ai:thread:read` is
   * exactly what the endpoint behind this bar enforces, so it cannot disagree.
   */
  const aiAvailable = hasPermission('ai:thread:read');

  const [aiOpen, setAiOpen] = useState(false);
  const [aiInitialPrompt, setAiInitialPrompt] = useState<string | null>(null);
  const [aiInitialThreadId, setAiInitialThreadId] = useState<string | null>(null);
  const [aiContextOverride, setAiContextOverride] = useState<{
    pluginId: string;
    entityType: string;
    entityId: string;
    url: string;
  } | null>(null);
  const [aiContextSession, setAiContextSession] = useState(0);
  const [aiActingCompany, setAiActingCompany] = useState<string | null>(null);
  /*
    What the open page says the chat works on, for as long as it is open: the
    company a platform admin picked (they have none of their own) and the
    record on screen when the URL does not name it. Without it, the Ask bar on
    such a page started a chat with no company, which the server refused.
  */
  const [pageAi, setPageAi] = useState<{
    companyId: string | null;
    pageContext: { pluginId: string; entityType: string; entityId: string; url: string } | null;
  }>({ companyId: null, pageContext: null });
  // The event listeners are bound once; they read the current values here.
  const pageAiCompanyRef = useRef<string | null>(null);
  const aiActingCompanyRef = useRef<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const pageContext = deriveAiPageContext(location.pathname, location.search);

  useKeyboardShortcut({
    key: 'k',
    metaKey: true,
    ctrlKey: true,
    onTrigger: () => setPaletteOpen((o) => !o),
  });

  useEffect(() => {
    const h = (e: Event) => {
      const ce = e as CustomEvent<{
        prompt?: string;
        pageContext?: {
          pluginId: string;
          entityType: string;
          entityId: string;
          url: string;
        };
        /** A platform admin's chat runs for this company (they have none of their own). */
        companyId?: string;
      }>;
      const actingFor = ce.detail?.companyId ?? null;
      // Before the chat mounts, so its first request already names the company.
      setActingCompany(actingFor ?? pageAiCompanyRef.current);
      aiActingCompanyRef.current = actingFor;
      setAiActingCompany(actingFor);
      if (ce.detail?.prompt) setAiInitialPrompt(ce.detail.prompt);
      setAiContextOverride(ce.detail?.pageContext ?? null);
      if (ce.detail?.pageContext || actingFor) setAiContextSession((current) => current + 1);
      setAiOpen(true);
    };
    window.addEventListener('lognai:open-ai-chat', h);
    return () => window.removeEventListener('lognai:open-ai-chat', h);
  }, []);

  useEffect(() => {
    const h = (e: Event) => {
      const ce = e as CustomEvent<{
        companyId?: string;
        pageContext?: { pluginId: string; entityType: string; entityId: string; url: string };
      } | null>;
      const companyId = ce.detail?.companyId ?? null;
      const changedCompany = companyId !== pageAiCompanyRef.current;
      pageAiCompanyRef.current = companyId;
      setPageAi({ companyId, pageContext: ce.detail?.pageContext ?? null });
      // A chat opened for a named company keeps it.
      if (!aiActingCompanyRef.current) setActingCompany(companyId);
      // Another company's threads are not this one's: start the chat afresh.
      if (changedCompany) setAiContextSession((current) => current + 1);
    };
    window.addEventListener('lognai:ai-page-context', h);
    return () => window.removeEventListener('lognai:ai-page-context', h);
  }, []);

  /**
   * Is an external tool switched on for the active company?
   *
   * ProcureAI and FleetMon are separate applications linked from the shell, not
   * modules of this one. They used to render unconditionally for every internal
   * user, so every customer saw them whether or not they had bought them.
   *
   * Each now has a stub backend plugin whose only job is to carry the
   * entitlement, which puts it in the per-company plugin checklist alongside the
   * real modules (Admin -> Companies -> Plugins). `effectivePlugins` is
   * preferred over `enabledPlugins` because it is the partnership-resolved set —
   * the same one App.tsx loads plugin bundles from.
   */
  const entitledPlugins = company?.effectivePlugins?.length
    ? company.effectivePlugins
    : (company?.enabledPlugins ?? []);
  const hasExternalTool = (pluginId: string): boolean => entitledPlugins.includes(pluginId);
  const canViewCommandCenter = hasPermission('shipment:shipment:read');

  /**
   * App-level nav items, keyed by the section they belong to.
   *
   * These are the entries no plugin owns — the workspace home, the AI surfaces,
   * settings, and the entitled external tools. They used to be a static list
   * concatenated around the plugin items, with FleetMon and ProcureAI spliced
   * in by searching for the literal `header-shipments`/`separator-shipments`
   * ids; a renamed section made that `findIndex` return -1 and the links
   * disappeared with no error. Handing them to `useNavigation` by section id
   * instead means placement follows the same section/order rules as everything
   * else.
   *
   * External partner users get a minimal portal — only their own plugin pages —
   * so they get none of these.
   */
  const sectionExtras = React.useMemo<Record<string, NavItemConfig[]> | undefined>(() => {
    if (isExternalPartner) return undefined;
    return {
      workspace: [
        {
          id: 'workspace',
          label: 'My workspace',
          path: '/workspace',
          icon: <PanelsTopLeft size={20} />,
          order: 1,
        },
        {
          id: 'drafts',
          label: 'Drafts',
          path: '/drafts',
          icon: <Inbox size={20} />,
          order: 3,
        },
      ],
      // A full-screen page outside this layout, reached straight from the rail.
      'command-center': canViewCommandCenter
        ? [
            {
              id: 'command-center',
              label: 'Command Center',
              path: '/command-center',
              icon: <LiveDot />,
              order: 1,
            },
          ]
        : [],
      'procure-ai': hasExternalTool('procureai')
        ? [
            {
              id: 'procureai',
              label: 'ProcureAI',
              path: 'https://procureai.lognai.com/',
              icon: <ShoppingCart size={20} />,
              external: true,
              order: 5,
            },
          ]
        : [],
      insights: [
        {
          id: 'insights',
          label: 'Insights',
          path: '/insights',
          icon: <Sparkles size={20} />,
          order: 1,
          badge: (
            <span
              className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none tracking-wide text-white"
              style={{ background: 'var(--accent)' }}
            >
              AI
            </span>
          ),
        },
      ],
      admin: [
        {
          id: 'settings',
          label: 'Settings',
          path: '/settings',
          icon: <Settings size={20} />,
          order: 3,
        },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExternalPartner, canViewCommandCenter, entitledPlugins.join(',')]);

  // Get dynamic navigation from route registry
  const { navItems: navItemConfigs, isLoading } = useNavigation({ sectionExtras });

  // The crumb's record. A sectioned plugin's detail page is looked up for its
  // label only; it is not part of the AI page context (see deriveSectionEntity).
  const breadcrumbEntity =
    pageContext ?? deriveSectionEntity(location.pathname, isRegisteredStaticSegment);
  const { label: entityBreadcrumbLabel } = useEntityLabel(breadcrumbEntity ?? {});

  // Registered route patterns, pre-split into segments for the breadcrumb
  // clickability check. Keyed on navItems: the registry is populated by the
  // same plugin-loading pass that produces the nav, so a nav change is the
  // signal that routes (re)registered.
  const routePatterns = React.useMemo(
    () => getAllRoutes().map((r) => r.path.split('/').filter(Boolean)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navItemConfigs]
  );

  // Routes that declared a `parentPath`, so a detail page whose URL does not
  // repeat its list page's segment still crumbs through the list page.
  const routeParents = React.useMemo<RouteParent[]>(() => {
    const all = getAllRoutes();
    const byId = new Map(all.map((r) => [r.id, r.path]));
    return all.flatMap((r) => {
      const parentPath = r.parentId ? byId.get(r.parentId) : undefined;
      return parentPath ? [{ pattern: r.path.split('/').filter(Boolean), parentPath }] : [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navItemConfigs]);

  // Set by the Not-Found page when it is what rendered — see `route-outcome`.
  const [routeNotFound, setRouteNotFound] = React.useState(false);

  const derivedBreadcrumbs = React.useMemo(
    () =>
      buildBreadcrumbs(
        pageContext?.pluginId === 'analytics' && pageContext.entityType === 'dataset'
          ? `/analytics/datasets/${encodeURIComponent(pageContext.entityId)}`
          : location.pathname,
        navItemConfigs,
        navigate,
        routePatterns,
        pageContext?.pluginId === 'analytics' && pageContext.entityType === 'dataset'
          ? encodeURIComponent(pageContext.entityId)
          : breadcrumbEntity?.entityId,
        entityBreadcrumbLabel,
        { sectionRoots: SECTION_ROOTS, routeParents }
      ),
    [
      location.pathname,
      pageContext?.pluginId,
      pageContext?.entityType,
      navItemConfigs,
      navigate,
      routePatterns,
      routeParents,
      breadcrumbEntity?.entityId,
      entityBreadcrumbLabel,
    ]
  );

  // A made-up path used to render a confident trail derived from its own
  // segments — "Home › Admin › All Roles" above a 404 body — which reads as if
  // the page exists and is merely empty (QA ledger NAV-002).
  const breadcrumbItems = routeNotFound
    ? [{ label: 'Home', onClick: () => navigate('/dashboard') }, { label: 'Not Found' }]
    : derivedBreadcrumbs;

  // Convert NavItemConfig[] to NavItem[] for AppShell
  const convertToNavItems = (items: any[]): NavItem[] => {
    return items.map((item) => ({
      id: item.id,
      href: item.path || '/',
      icon: item.icon,
      text: item.label,
      isSeparator: item.isSeparator,
      isGroupHeader: item.isGroupHeader,
      description: item.description,
      isCaption: item.isCaption,
      startsBlock: item.startsBlock,
      external: item.external,
      badge: item.badge,
      disabled: item.disabled,
      matchPaths: item.matchPaths,
      subItems: item.children
        ? item.children.map((child: any) => ({
            href: child.path || '/',
            text: child.label,
          }))
        : undefined,
    }));
  };

  // Every entry — plugin routes and app-level extras alike — is already placed
  // and grouped by `useNavigation`, so the shell just converts the flat list.
  const allNavItems: NavItem[] = convertToNavItems(navItemConfigs);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div>Loading...</div>
      </div>
    );
  }

  const analyticsPresentation =
    location.pathname.startsWith('/analytics/dashboards/') &&
    new URLSearchParams(location.search).get('present') === '1';

  if (analyticsPresentation) {
    return (
      <div className="min-h-screen w-full bg-background">
        <Outlet />
      </div>
    );
  }

  return (
    <AppShell
      title=""
      currentPath={location.pathname}
      LinkComponent={RouterLink as never}
      navigationItems={allNavItems}
      collapsed={sidebarCollapsed}
      onToggleCollapsed={toggleCollapsed}
      logo={<Logo variant={logoVariant} className="h-[38px] w-auto" />}
      collapsedLogo={
        // Same surface derivation as the expanded logo above: the collapsed rail
        // is the same surface, so the mark must not hard-code a variant. The
        // default `white` painted its card frame white-on-white in light mode.
        brand.mark ? (
          // The rail's logo box is 80×56; let a square mark or a wide
          // wordmark both fit without being crushed into a fixed square.
          <CompanyBrandImage
            src={brand.mark}
            alt={`${company?.name ?? 'Company'} logo`}
            className="max-h-9 w-auto max-w-[64px] object-contain"
            fallback={lognaiMark}
          />
        ) : (
          lognaiMark
        )
      }
      sidebarFooter={brand.active ? <PoweredByLognAI variant={logoVariant} /> : undefined}
      headerContent={
        <>
          {breadcrumbItems.length > 0 && (
            <Breadcrumbs items={breadcrumbItems} className="min-w-0 overflow-hidden md:shrink-0" />
          )}
          {aiAvailable && (
          <><IconButton className="ml-auto shrink-0 md:hidden" icon={Sparkles} aria-label="Ask LognAI" onClick={() => { setAiInitialThreadId(null); setAiOpen(true); }} />{/* The bar sizes itself as a % of its parent; this wrapper shrink-wraps,
              so it carries the header-relative width and the bar fills it. */}
          <div className="ml-auto hidden md:block md:w-[calc(32%-16px)]"><AskLognAISearchBar
            className="!w-full"
            // The module the user is in, so the example questions are about
            // what is on screen (QA ledger FA-007).
            moduleId={location.pathname.split('/').filter(Boolean)[0]}
            onClick={(query) => {
              setAiInitialThreadId(null);
              if (query) setAiInitialPrompt(query);
              setAiOpen(true);
            }}
            onOpenThread={(threadId) => {
              setAiInitialPrompt(null);
              setAiInitialThreadId(threadId);
              setAiOpen(true);
            }}
          /></div></>
          )}
          {/* The search bar carried `ml-auto`; without it the user menu needs
              its own spacer or the header collapses to the left. */}
          {!aiAvailable && <div className="ml-auto" />}
          <UserMenu user={user} />
        </>
      }
    >
      <div className="w-full px-6 py-6">
        <RouteOutcomeProvider notFound={routeNotFound} setNotFound={setRouteNotFound}>
          <Outlet />
        </RouteOutcomeProvider>
      </div>
      {aiAvailable && (
      <ChatSidebar
        key={
          aiContextOverride || aiActingCompany || pageAi.companyId
            ? `context-${aiContextSession}`
            : 'global'
        }
        open={aiOpen}
        onClose={() => {
          setAiOpen(false);
          setAiInitialPrompt(null);
          setAiInitialThreadId(null);
          setAiContextOverride(null);
          setActingCompany(pageAiCompanyRef.current);
          aiActingCompanyRef.current = null;
          setAiActingCompany(null);
        }}
        pageContext={aiContextOverride ?? pageAi.pageContext ?? pageContext}
        initialPrompt={aiInitialPrompt ?? undefined}
        initialThreadId={aiInitialThreadId ?? undefined}
      />
      )}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={(target) => navigate(target)}
        onAskAi={
          aiAvailable
            ? (q) => {
                setPaletteOpen(false);
                setAiInitialPrompt(q);
                setAiOpen(true);
              }
            : undefined
        }
      />
    </AppShell>
  );
}
